/**
 * What goes into a coffee after the counter hands it over.
 *
 * A latte's published figures stop at the cup. The two sugars stirred in
 * afterwards are 32 kcal that no chain has any reason to know about, and they
 * are the difference between a drink that costs nothing and one that costs a
 * tenth of a day's deficit. So they are kept here rather than in any chain's
 * catalogue: the same spoon of sugar is the same spoon of sugar at Circle K, at
 * Caffeine, and at the kitchen table.
 *
 * The figures are the standard composition of the ingredient — sucrose is 400
 * kcal per 100 g wherever it is poured from — and the unit is the spoon or
 * splash a person actually uses, not a weight anyone measures. Where a chain
 * publishes its own additions those win, because a Circle K sugar stick is a
 * stated 3 g and this is a guess at a teaspoon.
 */

import { foldLt } from './lithuanian.js'
import type { Per100g } from './macros.js'

export interface DrinkExtra {
  key: string
  /** What it is called in the app, in the amount one tap adds. */
  name: string
  /** One tap's worth. Grams for spooned things, millilitres for poured ones. */
  unitG: number
  unit: 'g' | 'ml'
  per100g: Per100g
}

/**
 * Ordered by how often a coffee gets them. The list is deliberately short:
 * a chooser with twenty entries is one nobody scrolls, and anything unusual
 * enough to be missing is better logged as its own food.
 */
export const DRINK_EXTRAS: readonly DrinkExtra[] = [
  {
    key: 'sugar-white',
    name: 'Cukrus (šaukštelis)',
    unitG: 4,
    unit: 'g',
    per100g: { kcal: 400, protein: 0, carbs: 100, fat: 0, sugars: 100, satFat: 0, fiber: 0 },
  },
  {
    key: 'sugar-brown',
    name: 'Rudasis cukrus (šaukštelis)',
    unitG: 4,
    unit: 'g',
    per100g: { kcal: 380, protein: 0, carbs: 98, fat: 0, sugars: 97, satFat: 0, fiber: 0 },
  },
  {
    key: 'honey',
    name: 'Medus (šaukštelis)',
    unitG: 7,
    unit: 'g',
    per100g: { kcal: 304, protein: 0.3, carbs: 82, fat: 0, sugars: 82, satFat: 0, fiber: 0.2 },
  },
  {
    key: 'milk',
    name: 'Pienas 2,5 % (lašas, 30 ml)',
    unitG: 30,
    unit: 'ml',
    per100g: { kcal: 51, protein: 3.4, carbs: 4.8, fat: 2.5, sugars: 4.8, satFat: 1.6, fiber: 0 },
  },
  {
    key: 'oat',
    name: 'Avižų gėrimas (30 ml)',
    unitG: 30,
    unit: 'ml',
    per100g: { kcal: 45, protein: 0.3, carbs: 6.6, fat: 1.5, sugars: 4, satFat: 0.2, fiber: 0.8 },
  },
  {
    key: 'cream',
    name: 'Grietinėlė 10 % (20 ml)',
    unitG: 20,
    unit: 'ml',
    per100g: { kcal: 119, protein: 2.8, carbs: 4, fat: 10, sugars: 4, satFat: 6.4, fiber: 0 },
  },
  {
    key: 'syrup',
    name: 'Sirupas (10 ml)',
    unitG: 10,
    unit: 'ml',
    per100g: { kcal: 260, protein: 0, carbs: 65, fat: 0, sugars: 62, satFat: 0, fiber: 0 },
  },
]

/**
 * Whether a thing is drunk, which is the question of whether sugar can go in it.
 *
 * The unit answers it whenever the catalogue knows one; the name is the fallback
 * for a coffee that was typed in by hand or came off a photo, where nobody ever
 * said it was a liquid.
 */
const DRINK_NAME = /(kav[ao]|latte|espres|capp?u|kapu[čc]|americ|moka|arbat|čaj|chai|matcha)/i

/**
 * The espresso family: everything poured as a shot rather than as a cup.
 *
 * Vero Cafe's smallest cup is a 35 ml "Greitukas", which the builder was
 * offering on every drink it sells — a 35 ml latte is not a size, it is a
 * mistake, and a 500 ml espresso is the same mistake pointing the other way.
 * Both come from treating a shot glass as a cup, and both disappear once the
 * two are told apart.
 */
const SHOT_DRINK =
  /espres|ristret|makiat|macchiat|dopio|doppio|\bšotas?\b|\bsotas?\b|dvigub(a|as)? espres/i

/**
 * Anything this small is a shot glass, not a small cup.
 *
 * The threshold has to clear a double espresso's 70 ml and stay well under the
 * smallest real cup anyone pours, which is 200. Ninety sits in the gap with
 * room on both sides.
 */
export const SHOT_CUP_MAX_ML = 90

export function isShotDrink(name: string): boolean {
  // A macchiato with milk in the name is a latte macchiato, which is a cup.
  if (/latte\s*macchiat|lat[eė]\s*makiat/i.test(name)) return false
  return SHOT_DRINK.test(name)
}

/**
 * The cups a given drink is actually poured into.
 *
 * A shot drink gets the shot glasses and a cup drink gets the cups; where a
 * chain publishes only one kind, every size stays on offer rather than leaving
 * a drink with no volume at all to apply its per-100 ml figures to.
 */
export function cupsForDrink<T extends { ml: number }>(
  name: string,
  cups: readonly T[],
): readonly T[] {
  const shots = cups.filter((cup) => cup.ml <= SHOT_CUP_MAX_ML)
  if (shots.length === 0 || shots.length === cups.length) return cups
  const wanted = isShotDrink(name) ? shots : cups.filter((cup) => cup.ml > SHOT_CUP_MAX_ML)
  return wanted.length > 0 ? wanted : cups
}

export function looksLikeDrink(name: string, unit?: 'g' | 'ml' | null): boolean {
  if (unit === 'ml') return true
  return DRINK_NAME.test(name)
}

export const WATER_NAME = 'Vanduo'

export function isWaterName(name: string): boolean {
  return foldLt(name.trim()) === 'vanduo'
}

export function isDrinkOrWaterName(name: string): boolean {
  return isWaterName(name) || looksLikeDrink(name)
}
